import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  Briefcase,
  Users,
  Heart,
  Rocket,
  CheckCircle
} from 'lucide-react';

const Careers = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real implementation, this would connect to a backend service
    // For now, we'll just simulate a successful submission
    setSubmitted(true);
    setEmail('');
  };
  
  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SEO
        title="Careers — Join Engaged Manila"
        description="Explore career opportunities at Engaged Manila. Join the team building the Philippines' leading influencer marketing and product review platform."
        url="/careers"
      />
      <Header />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary/90 to-blue-800 pt-28 pb-20 overflow-hidden">
        {/* Abstract geometric background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Hexagon pattern */}
          <svg className="absolute right-0 top-0 h-full w-1/2 text-white/5" viewBox="0 0 150 400" fill="none">
            <motion.path 
              d="M149.5 0V150L112.5 175V325L75.5 350V200L37.5 175V25L0.5 0V150L37.5 175V325L75.5 350V200L112.5 175V25L149.5 0Z" 
              fill="currentColor"
              animate={{ opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
          </svg>
          
          {/* Circles */}
          <svg className="absolute left-0 bottom-0 w-1/2 h-1/2 text-white/5" viewBox="0 0 200 200" fill="none">
            <motion.circle 
              cx="100" 
              cy="100" 
              r="50" 
              stroke="currentColor" 
              strokeWidth="2"
              strokeDasharray="314"
              strokeDashoffset="0"
              fill="none"
              animate={{ strokeDashoffset: [0, 314, 0] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            />
            <motion.circle 
              cx="100" 
              cy="100" 
              r="70" 
              stroke="currentColor" 
              strokeWidth="1.5"
              strokeDasharray="440"
              strokeDashoffset="0"
              fill="none"
              animate={{ strokeDashoffset: [0, -440, 0] }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            />
          </svg>
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0" 
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3Ccircle cx='13' cy='13' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '20px 20px'
            }}
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center justify-center px-4 py-1.5 mb-6 rounded-full bg-blue-400/10 backdrop-blur-sm border border-blue-300/20">
              <span className="text-sm font-medium text-blue-100">Engaged Manila</span>
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              <span className="block">Join Our Team</span>
            </h1>
            <p className="mt-6 text-xl leading-8 text-blue-100">
              Help us shape the future of authentic influencer marketing in the Philippines
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="py-24 bg-gray-50 flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center mb-8 h-16 w-16 rounded-full bg-primary/10 ring-1 ring-primary/20">
                <Briefcase className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                We're Building Our Team
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
                While we don't have any open positions at the moment, we're always looking for talented individuals who are passionate about influencer marketing and technology.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <a
                  href="mailto:careers@engagedmanila.com"
                  className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-all duration-200"
                >
                  Contact Recruiting Team
                </a>
              </div>
            </div>
          </motion.div>
          
          {/* Company Values */}
          <div className="mt-32">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">Why Work With Us</h2>
              <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                Join a team that's redefining influencer marketing in the Philippines
              </p>
            </motion.div>
            
            <motion.div 
              className="grid grid-cols-1 gap-y-12 gap-x-8 sm:grid-cols-2 lg:grid-cols-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="relative group">
                <div className="absolute -inset-x-4 -inset-y-4 z-0 rounded-2xl bg-gray-50 group-hover:bg-gray-100/80 transition duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 text-primary mb-6">
                    <Rocket className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Innovation-Driven</h3>
                  <p className="mt-3 text-base leading-7 text-gray-600">
                    We're constantly pushing boundaries to create solutions that transform how brands and influencers collaborate.
                  </p>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute -inset-x-4 -inset-y-4 z-0 rounded-2xl bg-gray-50 group-hover:bg-gray-100/80 transition duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 text-primary mb-6">
                    <Users className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Collaborative Culture</h3>
                  <p className="mt-3 text-base leading-7 text-gray-600">
                    Work with a diverse team of professionals who value creativity, respect, and teamwork in a supportive environment.
                  </p>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute -inset-x-4 -inset-y-4 z-0 rounded-2xl bg-gray-50 group-hover:bg-gray-100/80 transition duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 text-primary mb-6">
                    <Heart className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Growth Opportunities</h3>
                  <p className="mt-3 text-base leading-7 text-gray-600">
                    Develop your skills and advance your career in a fast-growing startup focused on personal and professional growth.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Email Notification Form */}
          <motion.div 
            className="mt-32 relative isolate overflow-hidden bg-white rounded-3xl shadow-sm border border-gray-200 py-16 sm:py-24 lg:py-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="absolute inset-0 -z-10 overflow-hidden">
              <svg
                className="absolute left-[max(50%,25rem)] top-0 h-[64rem] w-[128rem] -translate-x-1/2 stroke-gray-200 [mask-image:radial-gradient(64rem_64rem_at_top,white,transparent)]"
                aria-hidden="true"
              >
                <defs>
                  <pattern
                    id="e813992c-7d03-4cc4-a2bd-151760b470a0"
                    width="200"
                    height="200"
                    x="50%"
                    y="-1"
                    patternUnits="userSpaceOnUse"
                  >
                    <path d="M100 200V.5M.5 .5H200" fill="none" />
                  </pattern>
                </defs>
                <svg x="50%" y="-1" className="overflow-visible fill-gray-50">
                  <path
                    d="M-100.5 0h201v201h-201Z M699.5 0h201v201h-201Z M499.5 400h201v201h-201Z M-300.5 600h201v201h-201Z"
                    strokeWidth="0"
                  />
                </svg>
                <rect width="100%" height="100%" strokeWidth="0" fill="url(#e813992c-7d03-4cc4-a2bd-151760b470a0)" />
              </svg>
            </div>
            
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Stay Updated on Career Opportunities</h2>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Be the first to know when we have new positions available at Engaged Manila.
              </p>
              
              {!submitted ? (
                <form onSubmit={handleSubmit} className="mt-10 max-w-md mx-auto">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-grow">
                      <label htmlFor="career-email" className="sr-only">Email address</label>
                      <input
                        id="career-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <button
                      type="submit"
                      className="flex items-center justify-center px-5 py-3 border border-transparent rounded-md shadow-sm text-base font-semibold text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
                    >
                      <span className="flex items-center">
                        Notify Me
                      </span>
                    </button>
                  </div>
                  <p className="mt-4 text-sm text-gray-500">We respect your privacy and will only use your email for career notifications.</p>
                </form>
              ) : (
                <div className="mt-10 max-w-md mx-auto bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-green-500" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <p className="text-base font-medium text-green-800">
                        Thank you! We'll notify you when new positions are available.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
          
          {/* Return to Home */}
          <div className="mt-16 text-center">
            <Link 
              to="/"
              className="inline-flex items-center text-primary hover:text-primary-dark font-medium transition-colors duration-200"
            >
              <span>Return to Home</span>
            </Link>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Careers;
